import { useEffect, useState, type MouseEvent, type ReactNode } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { EstadoError, EstadoVacio } from '@/components/ui/estado-vacio';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils/cn';

/**
 * Tabla que se apila (vault 349, base para llevar la app al teléfono).
 *
 * En la PC, la tabla de siempre. En el teléfono (bajo 768 px, el mismo corte que el menú
 * cajón), una tarjeta por fila: lo que importa arriba y el resto como «rótulo: valor», sin
 * desplazamiento lateral. Cada columna dice dónde va en la tarjeta con `enTarjeta`:
 * - `titulo`: arriba, a lo ancho (el nombre del producto, la persona);
 * - `acciones`: arriba a la derecha, sin rótulo (editar, menú «…»);
 * - `dato` (por omisión): en la grilla de dos columnas, con su encabezado como rótulo;
 * - `oculta`: no entra en la tarjeta.
 *
 * Uso: `DataTable enTelefono="tarjetas"` con esas marcas en sus columnas, o, para una tabla
 * escrita a mano, `TablaQueSeApila` con la tabla de siempre en `tabla` (la PC no cambia).
 */
export type LugarEnTarjeta = 'titulo' | 'acciones' | 'dato' | 'oculta';

export interface ColumnaApilable<T> {
  id: string;
  encabezado: ReactNode;
  celda: (fila: T) => ReactNode;
  /** Dónde va en la tarjeta del teléfono. Por omisión, `dato`. */
  enTarjeta?: LugarEnTarjeta;
  /** El rótulo en la tarjeta, si el encabezado no es un texto que sirva. */
  rotulo?: string;
}

/** El corte del teléfono de toda la app: el mismo `md` de Tailwind donde aparece el cajón. */
export const CONSULTA_DE_TELEFONO = '(max-width: 767px)';

const consultar = (consulta: string): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(consulta).matches;

/**
 * ¿La pantalla es de teléfono? Sigue al tamaño de la ventana (girar el teléfono cuenta).
 * Sin `matchMedia` (jsdom en las pruebas) dice que no: las pruebas ven la versión de PC.
 */
export function useEsTelefono(consulta = CONSULTA_DE_TELEFONO): boolean {
  const [telefono, setTelefono] = useState(() => consultar(consulta));
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(consulta);
    const alCambiar = () => setTelefono(mq.matches);
    alCambiar();
    mq.addEventListener?.('change', alCambiar);
    return () => mq.removeEventListener?.('change', alCambiar);
  }, [consulta]);
  return telefono;
}

export interface TarjetasDeTablaProps<T> {
  'aria-label': string;
  columnas: Array<ColumnaApilable<T>>;
  filas: T[];
  obtenerId: (fila: T) => string;
  /** Cómo se llama la fila («Samsung A15»): nombra su casilla y su botón para abrirla. */
  nombreDeFila?: (fila: T) => string;
  estado?: 'listo' | 'cargando' | 'error';
  mensajeDeError?: ReactNode;
  onReintentar?: () => void;
  vacio?: ReactNode;
  seleccion?: { elegidos: Set<string>; onCambiar: (elegidos: Set<string>) => void };
  onFilaClick?: (fila: T) => void;
  /** La fila abierta en un panel: lleva la marca del primario. */
  filaActiva?: string;
  filasDeEsqueleto?: number;
  className?: string;
}

const sinPropagar = (e: MouseEvent) => e.stopPropagation();

/** Las filas como tarjetas apiladas (la versión de teléfono de una tabla). */
export function TarjetasDeTabla<T>({
  'aria-label': etiqueta,
  columnas,
  filas,
  obtenerId,
  nombreDeFila,
  estado = 'listo',
  mensajeDeError = 'No pudimos cargar la lista.',
  onReintentar,
  vacio,
  seleccion,
  onFilaClick,
  filaActiva,
  filasDeEsqueleto = 4,
  className,
}: TarjetasDeTablaProps<T>) {
  if (estado === 'error') {
    return (
      <div className={cn('p-4', className)}>
        <EstadoError onReintentar={onReintentar}>{mensajeDeError}</EstadoError>
      </div>
    );
  }
  if (estado === 'cargando') {
    return (
      <div className={cn('flex flex-col gap-3 p-4', className)} aria-busy="true">
        {Array.from({ length: filasDeEsqueleto }, (_, i) => (
          <Skeleton key={i} data-testid="tarjeta-esqueleto" className="h-24 w-full rounded-xl" />
        ))}
      </div>
    );
  }
  if (filas.length === 0) {
    return (
      <div className={cn('p-4', className)}>
        {vacio ?? <EstadoVacio titulo="Nada para mostrar" />}
      </div>
    );
  }

  const lugar = (c: ColumnaApilable<T>) => c.enTarjeta ?? 'dato';
  const titulos = columnas.filter((c) => lugar(c) === 'titulo');
  const acciones = columnas.filter((c) => lugar(c) === 'acciones');
  const datos = columnas.filter((c) => lugar(c) === 'dato');

  const alternar = (id: string) => {
    if (!seleccion) return;
    const siguiente = new Set(seleccion.elegidos);
    if (siguiente.has(id)) siguiente.delete(id);
    else siguiente.add(id);
    seleccion.onCambiar(siguiente);
  };

  return (
    <ul aria-label={etiqueta} className={cn('divide-y', className)}>
      {filas.map((fila) => {
        const id = obtenerId(fila);
        const nombre = nombreDeFila?.(fila);
        const elegida = seleccion?.elegidos.has(id) ?? false;
        const cabeza = titulos.map((c) => <div key={c.id}>{c.celda(fila)}</div>);
        return (
          <li
            key={id}
            data-state={elegida ? 'selected' : undefined}
            onClick={onFilaClick ? () => onFilaClick(fila) : undefined}
            className={cn(
              'flex gap-3 px-4 py-4 data-[state=selected]:bg-primary/5',
              onFilaClick && 'cursor-pointer active:bg-muted/50',
              filaActiva === id && 'shadow-[inset_3px_0_0_hsl(var(--primary))]'
            )}
          >
            {seleccion && (
              // La casilla con su área de toque de 44 px (la etiqueta la prende); no abre la fila.
              <label
                className="-m-2 flex size-11 shrink-0 items-start justify-center pt-2.5"
                onClick={sinPropagar}
              >
                <Checkbox
                  checked={elegida}
                  onCheckedChange={() => alternar(id)}
                  aria-label={nombre ? `Elegir ${nombre}` : 'Elegir fila'}
                />
              </label>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  {onFilaClick ? (
                    // El botón es la entrada del teclado; el clic sube hasta la tarjeta.
                    <button
                      type="button"
                      aria-label={nombre ? `Abrir ${nombre}` : 'Abrir'}
                      className="block w-full min-h-11 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {cabeza}
                    </button>
                  ) : (
                    cabeza
                  )}
                </div>
                {acciones.length > 0 && (
                  <div className="flex shrink-0 items-center gap-1" onClick={sinPropagar}>
                    {acciones.map((c) => (
                      <div key={c.id}>{c.celda(fila)}</div>
                    ))}
                  </div>
                )}
              </div>
              {datos.length > 0 && (
                <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3">
                  {datos.map((c) => (
                    <div key={c.id} className="min-w-0">
                      <dt className="text-xs font-medium text-muted-foreground">
                        {c.rotulo ?? c.encabezado}
                      </dt>
                      <dd className="mt-0.5 text-sm">{c.celda(fila)}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Para una tabla escrita a mano: en la PC muestra `tabla` tal cual; en el teléfono, las
 * mismas filas como tarjetas (con las columnas declaradas para la tarjeta).
 */
export function TablaQueSeApila<T>({
  tabla,
  ...props
}: TarjetasDeTablaProps<T> & { tabla: ReactNode }) {
  const telefono = useEsTelefono();
  return telefono ? <TarjetasDeTabla {...props} /> : <>{tabla}</>;
}
